import React, { FC } from 'react';

import { Box, Divider, Typography } from '@mui/material';

import { convertToDecimals, currency } from '@pharmaplan/common';

import WithSkeleton from '../../common/WithSkeleton';
import strings from '../../../localization';
import ServiceCard from '../ServiceCard';

import styles from './styles';

interface ILongTermBillDetails {
  successAction: string;
  details: {
    longTermShiftContract?: {
      weekDayPaidHourlyRate: number;
      weekendPaidHourlyRate: number;
    };
    totalWeekHours: string | number;
    totalWeekendHours: string | number;
    counterShiftContract?: {
      weekDayPaidHourlyRate: number;
      weekendPaidHourlyRate: number;
    };
  };
  customTitle?: string;
}

const LongTermBillDetails: FC<ILongTermBillDetails> = ({
  successAction,
  details,
  customTitle,
}) => {
  const {
    longTermShiftContract,
    totalWeekHours,
    totalWeekendHours,
    counterShiftContract,
  } = details ?? {};

  const {
    weekDayPaidHourlyRate: counterWeekRate,
    weekendPaidHourlyRate: counterWeekendRate,
  } = counterShiftContract ?? {};
  const { weekDayPaidHourlyRate, weekendPaidHourlyRate } = longTermShiftContract ?? {};

  const SkeletonTypography = WithSkeleton({ successAction, variant: 'text' })(
    Typography,
  );

  const weekRate = counterWeekRate ?? weekDayPaidHourlyRate ?? 0;
  const weekendRate = counterWeekendRate ?? weekendPaidHourlyRate ?? 0;

  const weekdayValue = `${currency}${weekRate} X ${parseFloat(
    totalWeekHours as string,
  )}${strings.perHour}`;
  const weekendValue = `${currency}${weekendRate} X ${parseFloat(
    totalWeekendHours as string,
  )}${strings.perHour}`;

  const total = weekRate * parseFloat(totalWeekHours as string)
    + weekendRate * parseFloat(totalWeekendHours as string);
  const totalValue = `${currency}${convertToDecimals(total)}`;

  return (
    <ServiceCard
      customContainerStyle={styles.container}
      title={customTitle || strings.billDetails}
      content={(
        <Box>
          {!!totalWeekHours && (
            <Box sx={styles.rowContainer}>
              <SkeletonTypography sx={styles.label}>
                {strings.weekdayBookingCost}
              </SkeletonTypography>
              <SkeletonTypography sx={styles.value}>
                {weekdayValue}
              </SkeletonTypography>
            </Box>
          )}

          {!!totalWeekendHours && (
            <Box sx={styles.rowContainer}>
              <SkeletonTypography sx={styles.label}>
                {strings.weekendBookingCost}
              </SkeletonTypography>
              <SkeletonTypography sx={styles.value}>
                {weekendValue}
              </SkeletonTypography>
            </Box>
          )}

          <Divider sx={styles.divider} />

          <Box sx={styles.rowContainer}>
            <SkeletonTypography sx={styles.label}>
              {strings.total}
            </SkeletonTypography>
            <SkeletonTypography sx={styles.value}>
              {totalValue}
            </SkeletonTypography>
          </Box>
        </Box>
      )}
    />
  );
};

export default LongTermBillDetails;
